/**
 * Message details dialog (DESIGN.md T10, "Message details"): the header analysis of one message,
 * explained for normal users, with a "Technical details" toggle for the raw values. Wired to
 * @platform/i18n (keys `mailDetails.*`) because the explanations are content, not labels.
 *
 * `MessageDetailsView` is the body without the dialog, so the admin app can show the same view in a
 * detail panel (message trace, quarantine, P9).
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import {
  CheckCircle2Icon,
  CircleHelpIcon,
  CopyIcon,
  FileIcon,
  FileTextIcon,
  ImageIcon,
  InfoIcon,
  LockIcon,
  LockOpenIcon,
  MailIcon,
  ServerIcon,
  ShieldAlertIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Badge } from '../components/badge';
import { Button } from '../components/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/dialog';
import { Alert, Skeleton } from '../components/misc';
import { toast } from '../components/sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/tabs';
import { DescriptionList, type DescriptionItem } from '../composites/detail-panel';
import { ErrorState, StatusBadge } from '../composites/states';
import { formatBytes } from '../lib/format';
import { PolicyEvaluationView } from './policy-evaluation';
import { ScannerHistorySection } from './scanner-history';
import { cn } from '../lib/utils';
import {
  resultStatus,
  SEVERITY_STATUS,
  type AnalysisAddress,
  type AnalysisAuthResult,
  type AnalysisFinding,
  type AnalysisHop,
  type AnalysisSeverity,
  type FindingCode,
  type MessageAnalysisView,
} from '../mail/analysis';

const FINDING_KEYS: Readonly<Record<FindingCode, MessageKey>> = {
  'source.exported': 'mailDetails.findings.source.exported',
  'source.reconstructed': 'mailDetails.findings.source.reconstructed',
  'source.headersOnly': 'mailDetails.findings.source.headersOnly',
  'delivery.local': 'mailDetails.findings.delivery.local',
  'delivery.noTls': 'mailDetails.findings.delivery.noTls',
  'delivery.slow': 'mailDetails.findings.delivery.slow',
  'auth.notChecked': 'mailDetails.findings.auth.notChecked',
  'auth.foreignResults': 'mailDetails.findings.auth.foreignResults',
  'auth.spfFail': 'mailDetails.findings.auth.spfFail',
  'auth.dkimFail': 'mailDetails.findings.auth.dkimFail',
  'auth.dmarcPass': 'mailDetails.findings.auth.dmarcPass',
  'auth.dmarcFail': 'mailDetails.findings.auth.dmarcFail',
  'scanner.clean': 'mailDetails.findings.scanner.clean',
  'scanner.infected': 'mailDetails.findings.scanner.infected',
  'scanner.notScanned': 'mailDetails.findings.scanner.notScanned',
  'scanner.released': 'mailDetails.findings.scanner.released',
  'scanner.missing': 'mailDetails.findings.scanner.missing',
  'sender.internal': 'mailDetails.findings.sender.internal',
  'sender.external': 'mailDetails.findings.sender.external',
  'sender.replyToDiffers': 'mailDetails.findings.sender.replyToDiffers',
  'sender.returnPathDiffers': 'mailDetails.findings.sender.returnPathDiffers',
  'sender.onBehalf': 'mailDetails.findings.sender.onBehalf',
  'sender.nameContainsAddress': 'mailDetails.findings.sender.nameContainsAddress',
  'sender.impersonatesInternal': 'mailDetails.findings.sender.impersonatesInternal',
  'sender.lookalikeDomain': 'mailDetails.findings.sender.lookalikeDomain',
  'sender.punycode': 'mailDetails.findings.sender.punycode',
};

const SEVERITY_ICON: Readonly<Record<AnalysisSeverity, { icon: LucideIcon; className: string }>> = {
  pass: { icon: CheckCircle2Icon, className: 'text-success' },
  info: { icon: InfoIcon, className: 'text-muted-foreground' },
  warning: { icon: TriangleAlertIcon, className: 'text-warning-foreground dark:text-warning' },
  fail: { icon: ShieldAlertIcon, className: 'text-destructive' },
};

const SEVERITY_ORDER: readonly AnalysisSeverity[] = ['fail', 'warning', 'info', 'pass'];

const AUTH_RESULTS =['pass', 'fail', 'softfail', 'neutral', 'none', 'temperror', 'permerror', 'policy'] as const;
const SCANNER_RESULTS = ['clean', 'infected', 'error', 'oversize', 'released'] as const;
const depthIndent = ['pl-0', 'pl-4', 'pl-8', 'pl-12', 'pl-16'] as const;

type Tab = 'overview' | 'senderAuthentication' | 'deliverySecurity' | 'technical';

function useLabels() {
  const { t, formatDate, formatNumber } = useI18n();
  const result = (value: string | null): string => {
    if (value === null) return t('mailDetails.auth.result.notChecked');
    return (AUTH_RESULTS as readonly string[]).includes(value) ? t(`mailDetails.auth.result.${value as (typeof AUTH_RESULTS)[number]}`) : value;
  };
  const duration = (seconds: number): string => {
    if (seconds < 60) return t('mailDetails.delivery.seconds', { value: seconds });
    if (seconds < 3600) return t('mailDetails.delivery.minutes', { value: formatNumber(seconds / 60, { maximumFractionDigits: 1 }) });
    return t('mailDetails.delivery.hours', { value: formatNumber(seconds / 3600, { maximumFractionDigits: 1 }) });
  };
  const date = (iso: string | null): string => (iso ? formatDate(iso, { dateStyle: 'medium', timeStyle: 'medium' }) : t('mailDetails.notAvailable'));
  const bytes = (n: number): string => formatBytes(n, formatNumber);
  return { t, result, duration, date, bytes };
}

function FindingList({ findings }: { findings: readonly AnalysisFinding[] }) {
  const { t } = useI18n();
  if (findings.length === 0) return null;
  // Most severe first (stable within a severity).
  const sorted = [...findings].sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));
  return (
    <ul className="flex flex-col gap-2">
      {sorted.map((f, i) => {
        const { icon: Icon, className } = SEVERITY_ICON[f.severity];
        return (
          <li key={`${f.code}-${i}`} className="flex items-start gap-2 text-sm">
            <Icon aria-hidden className={cn('mt-0.5 size-4 shrink-0', className)} />
            <span>
              <span className="sr-only">{t(`mailDetails.severity.${f.severity}`)}: </span>
              {t(FINDING_KEYS[f.code], f.params)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function Explain({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  return (
    <details className="group rounded-md border bg-muted/40 px-3 py-2 text-sm">
      <summary className="flex cursor-pointer list-none items-center gap-2 font-medium outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
        <CircleHelpIcon aria-hidden className="size-4 text-muted-foreground" />
        {t('mailDetails.whatDoesThisMean')}
      </summary>
      <p className="mt-2 text-muted-foreground">{children}</p>
    </details>
  );
}

/** Monospace raw value (expert view). */
function Raw({ children }: { children: ReactNode }) {
  return <pre className="overflow-x-auto rounded-md bg-muted p-2 font-mono text-xs break-all whitespace-pre-wrap">{children}</pre>;
}

const formatAddress = (a: AnalysisAddress | null): string => (a === null ? '' : a.name && a.name !== a.address ? `${a.name} <${a.address}>` : a.address);

function TabSection({ explain, findings, children }: { explain: string; findings: readonly AnalysisFinding[]; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-4">
      <FindingList findings={findings} />
      {children}
      <Explain>{explain}</Explain>
    </div>
  );
}

function SenderTab({ analysis, findings }: { analysis: MessageAnalysisView; findings: readonly AnalysisFinding[] }) {
  const { t } = useLabels();
  const { sender } = analysis;
  const items: DescriptionItem[] = [
    { label: t('mailDetails.sender.from'), value: formatAddress(sender.from) || t('mailDetails.notAvailable') },
    {
      label: t('mailDetails.sender.origin'),
      value: (
        <StatusBadge
          status={sender.external === null ? 'unknown' : sender.external ? 'warning' : 'ok'}
          label={t(sender.external === null ? 'mailDetails.sender.unknown' : sender.external ? 'mailDetails.sender.external' : 'mailDetails.sender.internal')}
        />
      ),
    },
  ];
  if (sender.sender) items.push({ label: t('mailDetails.sender.sender'), value: formatAddress(sender.sender) });
  if (sender.replyTo.length > 0) items.push({ label: t('mailDetails.sender.replyTo'), value: sender.replyTo.map(formatAddress).join(', ') });
  if (sender.returnPath) items.push({ label: t('mailDetails.sender.returnPath'), value: sender.returnPath });
  if (sender.fromDomainUnicode) items.push({ label: t('mailDetails.sender.unicodeDomain'), value: sender.fromDomainUnicode });
  return (
    <TabSection explain={t('mailDetails.sender.explain')} findings={findings}>
      <DescriptionList items={items} />
    </TabSection>
  );
}

function ResultBadge({ value, label }: { value: string | null; label: string }) {
  return <StatusBadge status={resultStatus(value)} label={label} />;
}

function AuthResultList({ results }: { results: readonly AnalysisAuthResult[] }) {
  const { result } = useLabels();
  return (
    <div className="flex flex-wrap gap-1.5">
      {results.map((r, i) => (
        <Badge key={`${r.method}-${i}`} variant="outline">
          {r.method.toUpperCase()}: {result(r.result)}
        </Badge>
      ))}
    </div>
  );
}

function AuthenticationTab({ analysis, findings, expert }: { analysis: MessageAnalysisView; findings: readonly AnalysisFinding[]; expert: boolean }) {
  const { t, result, date } = useLabels();
  const { authentication: auth } = analysis;
  const align = (a: 'strict' | 'relaxed' | 'none') => t(`mailDetails.auth.alignment.${a}`);
  const rows: DescriptionItem[] = [
    {
      label: t('mailDetails.auth.spf'),
      value: auth.spf ? (
        <span className="flex flex-wrap items-center gap-2">
          <ResultBadge value={auth.spf.result} label={result(auth.spf.result)} />
          {auth.spf.domain && <span>{auth.spf.domain}</span>}
          <span className="text-muted-foreground">
            {t('mailDetails.auth.alignmentLabel')}: {align(auth.spf.alignment)}
          </span>
        </span>
      ) : (
        <ResultBadge value={null} label={result(null)} />
      ),
    },
    {
      label: t('mailDetails.auth.dmarc'),
      value: auth.dmarc ? (
        <span className="flex flex-wrap items-center gap-2">
          <ResultBadge value={auth.dmarc.result} label={result(auth.dmarc.result)} />
          {auth.dmarc.fromDomain && <span>{auth.dmarc.fromDomain}</span>}
          {auth.dmarc.policy && (
            <span className="text-muted-foreground">
              {t('mailDetails.auth.policy')}: {auth.dmarc.policy}
            </span>
          )}
        </span>
      ) : (
        <ResultBadge value={null} label={result(null)} />
      ),
    },
  ];
  return (
    <TabSection explain={t('mailDetails.auth.explain')} findings={findings}>
      {!auth.verifiedByServer && (
        <Alert variant="warning">
          <TriangleAlertIcon />
          <span>{t('mailDetails.auth.notVerified')}</span>
        </Alert>
      )}
      <DescriptionList items={rows} />
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('mailDetails.auth.dkim')}</h3>
        {auth.dkim.length === 0 && <p className="text-sm text-muted-foreground">{t('mailDetails.auth.noSignature')}</p>}
        {auth.dkim.map((sig, i) => (
          <div key={`${sig.domain}-${sig.selector}-${i}`} className="flex flex-col gap-2 rounded-md border p-3">
            <div className="flex flex-wrap items-center gap-2">
              <ResultBadge value={sig.result} label={result(sig.result)} />
              <span className="font-medium">{sig.domain}</span>
            </div>
            <DescriptionList
              items={[
                { label: t('mailDetails.auth.selector'), value: sig.selector },
                { label: t('mailDetails.auth.alignmentLabel'), value: align(sig.alignment) },
                ...(sig.signedAt ? [{ label: t('mailDetails.auth.signedAt'), value: date(sig.signedAt) }] : []),
                ...(expert && sig.algorithm ? [{ label: t('mailDetails.auth.algorithm'), value: sig.algorithm }] : []),
                ...(expert ? [{ label: t('mailDetails.auth.signedHeaders'), value: <span className="font-mono text-xs">{sig.signedHeaders.join(', ')}</span> }] : []),
              ]}
            />
          </div>
        ))}
      </div>
      {(auth.arc.length > 0 || auth.foreign.length > 0) && (
        <div className="flex flex-col gap-2">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            {t('mailDetails.auth.foreign')} <StatusBadge status="unknown" label={t('mailDetails.auth.unverified')} />
          </h3>
          {auth.foreign.map((h, i) => (
            <div key={`foreign-${i}`} className="flex flex-col gap-1.5 rounded-md border p-3 text-sm">
              <span className="flex items-center gap-1.5 font-medium">
                <ServerIcon aria-hidden className="size-4 text-muted-foreground" />
                {h.authservId || t('mailDetails.auth.unknownServer')}
              </span>
              <AuthResultList results={h.results} />
              {expert && <Raw>{h.results.map((r) => `${r.method}=${r.result}${r.comment ? ` (${r.comment})` : ''}${Object.entries(r.props).map(([k, v]) => ` ${k}=${v}`).join('')}`).join('\n')}</Raw>}
            </div>
          ))}
          {auth.arc.map((set) => (
            <div key={`arc-${set.instance}`} className="flex flex-col gap-1.5 rounded-md border p-3 text-sm">
              <span className="font-medium">{t('mailDetails.auth.arcSet', { instance: set.instance, domain: set.sealDomain ?? set.authservId ?? '?' })}</span>
              {set.chainValidation && (
                <span className="text-muted-foreground">
                  {t('mailDetails.auth.chain')}: {set.chainValidation}
                </span>
              )}
              <AuthResultList results={set.results} />
            </div>
          ))}
        </div>
      )}
    </TabSection>
  );
}

function TlsBadge({ hop }: { hop: AnalysisHop }) {
  const { t } = useI18n();
  if (hop.tls.used === true) {
    return (
      <Badge variant="success">
        <LockIcon aria-hidden />
        {hop.tls.version ? t('mailDetails.delivery.tlsVersion', { version: hop.tls.version }) : t('mailDetails.delivery.tlsYes')}
      </Badge>
    );
  }
  if (hop.tls.used === false) {
    return (
      <Badge variant={hop.internal ? 'muted' : 'warning'}>
        <LockOpenIcon aria-hidden />
        {t('mailDetails.delivery.tlsNo')}
      </Badge>
    );
  }
  return <Badge variant="muted">{t('mailDetails.delivery.tlsUnknown')}</Badge>;
}

function DeliveryTab({ analysis, findings, expert }: { analysis: MessageAnalysisView; findings: readonly AnalysisFinding[]; expert: boolean }) {
  const { t, duration, date } = useLabels();
  const { delivery } = analysis;
  return (
    <TabSection explain={t('mailDetails.delivery.explain')} findings={findings}>
      <DescriptionList
        items={[
          { label: t('mailDetails.delivery.total'), value: delivery.totalSeconds === null ? t('mailDetails.notAvailable') : duration(delivery.totalSeconds) },
          { label: t('mailDetails.delivery.sentAt'), value: date(delivery.sentAt) },
          { label: t('mailDetails.delivery.deliveredAt'), value: date(delivery.deliveredAt) },
        ]}
      />
      {delivery.hops.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('mailDetails.delivery.noHops')}</p>
      ) : (
        <ol className="flex flex-col gap-4 border-l-2 pl-4">
          {delivery.hops.map((hop) => (
            <li key={hop.index} className="flex flex-col gap-1 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <ServerIcon aria-hidden className={cn('size-4', hop.ours ? 'text-primary' : 'text-muted-foreground')} />
                <span className="font-medium">{t('mailDetails.delivery.hop', { index: hop.index })}</span>
                <TlsBadge hop={hop} />
                {hop.ours && <Badge variant="info">{t('mailDetails.delivery.ourServer')}</Badge>}
                {hop.internal && !hop.ours && <Badge variant="muted">{t('mailDetails.delivery.internal')}</Badge>}
                {hop.delaySeconds !== null && <span className="text-xs text-muted-foreground">{t('mailDetails.delivery.delay', { duration: duration(hop.delaySeconds) })}</span>}
                {hop.clockSkew && <Badge variant="warning">{t('mailDetails.delivery.clockSkew')}</Badge>}
              </div>
              <span className="break-all">
                {t('mailDetails.delivery.from')}: {hop.fromHost ?? '–'}
                {hop.fromDetail ? ` (${hop.fromDetail})` : ''}
                {hop.fromIp ? ` [${hop.fromIp}]` : ''}
              </span>
              <span className="break-all">
                {t('mailDetails.delivery.by')}: {hop.byHost ?? '–'}
                {hop.protocol ? ` · ${hop.protocol}` : ''}
              </span>
              <span className="text-xs text-muted-foreground">{date(hop.receivedAt)}</span>
              {expert && hop.tls.cipher && (
                <span className="text-xs text-muted-foreground">
                  {t('mailDetails.delivery.cipher')}: <span className="font-mono">{hop.tls.cipher}</span>
                  {hop.tls.bits ? ` (${hop.tls.bits} bit)` : ''}
                </span>
              )}
              {expert && <Raw>Received: {hop.raw}</Raw>}
            </li>
          ))}
        </ol>
      )}
    </TabSection>
  );
}

function SecurityTab({ analysis, findings, expert }: { analysis: MessageAnalysisView; findings: readonly AnalysisFinding[]; expert: boolean }) {
  const { t } = useLabels();
  const { scanner } = analysis;
  const scannerResult = scanner?.result ?? null;
  const known = scannerResult !== null && (SCANNER_RESULTS as readonly string[]).includes(scannerResult);
  return (
    <TabSection explain={t('mailDetails.security.explain')} findings={findings}>
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('mailDetails.security.scanner')}</h3>
        {scanner === null ? (
          <p className="text-sm text-muted-foreground">{t('mailDetails.security.missing')}</p>
        ) : (
          <>
            {!scanner.trusted && <StatusBadge status="unknown" label={t('mailDetails.security.untrusted')} />}
            <DescriptionList
              items={[
                {
                  label: t('mailDetails.security.scanner'),
                  value: (
                    <StatusBadge
                      status={scannerResult === 'clean' ? 'ok' : scannerResult === 'infected' ? 'error' : 'warning'}
                      label={known ? t(`mailDetails.security.result.${scannerResult as (typeof SCANNER_RESULTS)[number]}`) : (scannerResult ?? '–')}
                    />
                  ),
                },
                ...(scanner.threat ? [{ label: t('mailDetails.security.threat'), value: scanner.threat }] : []),
                ...(scanner.engine ? [{ label: t('mailDetails.security.engine'), value: scanner.engine }] : []),
                ...(expert && scanner.scanId ? [{ label: t('mailDetails.security.scanId'), value: <span className="font-mono text-xs">{scanner.scanId}</span> }] : []),
              ]}
            />
          </>
        )}
        {analysis.policy && (
          <>
            <h3 className="text-sm font-semibold">{t('policyEvidence.title')}</h3>
            <PolicyEvaluationView mode="user" evaluation={analysis.policy.evaluation} ruleNames={analysis.policy.ruleNames} />
          </>
        )}
        {analysis.history && <ScannerHistorySection history={analysis.history} />}
        <Alert variant="info">{t('mailDetails.security.currentSourceSupplement')}</Alert>
      </div>
      {analysis.spam.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('mailDetails.security.other')}</h3>
          <DescriptionList
            items={analysis.spam.map((s) => ({
              label: s.header,
              value: <span className={cn('font-mono text-xs', !expert && 'line-clamp-2')}>{s.value}</span>,
            }))}
          />
        </div>
      )}
    </TabSection>
  );
}

function partIcon(contentType: string): LucideIcon {
  if (contentType.startsWith('image/')) return ImageIcon;
  if (contentType.startsWith('text/')) return FileTextIcon;
  if (contentType === 'message/rfc822') return MailIcon;
  return FileIcon;
}

function TechnicalTab({ analysis, findings, expert }: { analysis: MessageAnalysisView; findings: readonly AnalysisFinding[]; expert: boolean }) {
  const { t, date, bytes } = useLabels();
  const { message, source, structure } = analysis;
  const na = t('mailDetails.notAvailable');
  return (
    <TabSection explain={t('mailDetails.technical.explain')} findings={findings}>
      <DescriptionList
        items={[
          { label: t('mailDetails.technical.subject'), value: message.subject ?? na },
          { label: t('mailDetails.technical.date'), value: date(message.date) },
          { label: t('mailDetails.technical.messageId'), value: <span className="font-mono text-xs break-all">{message.messageId ?? na}</span> },
          ...(message.inReplyTo ? [{ label: t('mailDetails.technical.inReplyTo'), value: <span className="font-mono text-xs break-all">{message.inReplyTo}</span> }] : []),
          ...(expert && message.references.length > 0
            ? [{ label: t('mailDetails.technical.references'), value: <span className="font-mono text-xs break-all">{message.references.join(' ')}</span> }]
            : []),
          { label: t('mailDetails.technical.client'), value: message.client ?? na },
          { label: t('mailDetails.technical.size'), value: bytes(source.size) },
          { label: t('mailDetails.technical.source'), value: t(`mailDetails.technical.origin.${source.origin}`) },
        ]}
      />
      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('mailDetails.technical.structure')}</h3>
        {structure === null ? (
          <p className="text-sm text-muted-foreground">{t('mailDetails.technical.headersOnly')}</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm" aria-label={t('mailDetails.technical.structure')}>
            {structure.parts.map((part) => {
              const Icon = partIcon(part.contentType);
              return (
                <li key={part.path || 'root'} className={cn('flex flex-wrap items-center gap-x-2', depthIndent[Math.min(part.depth, depthIndent.length - 1)])}>
                  <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
                  <span className="font-mono text-xs">{part.contentType}</span>
                  {part.filename && <span className="font-medium">{part.filename}</span>}
                  {part.embedded?.subject && <span>{t('mailDetails.technical.embedded', { subject: part.embedded.subject })}</span>}
                  {!part.contentType.startsWith('multipart/') && <span className="text-xs text-muted-foreground">{bytes(part.size)}</span>}
                  {part.charset && <span className="text-xs text-muted-foreground">{part.charset}</span>}
                  {expert && part.transferEncoding && <span className="font-mono text-xs text-muted-foreground">{part.transferEncoding}</span>}
                  {expert && part.contentId && <span className="font-mono text-xs text-muted-foreground">cid:{part.contentId}</span>}
                </li>
              );
            })}
          </ul>
        )}
        {structure?.truncated && <p className="text-xs text-muted-foreground">{t('mailDetails.technical.structureTruncated')}</p>}
      </div>
    </TabSection>
  );
}

function HeadersTab({ analysis }: { analysis: MessageAnalysisView }) {
  const { t } = useI18n();
  const text = analysis.headers.map((h) => `${h.name}: ${h.value}`).join('\n');
  const copy = () => {
    void navigator.clipboard.writeText(text).then(() => toast.success(t('mailDetails.headers.copied')));
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t('mailDetails.headers.explain')}</p>
        <Button variant="outline" size="sm" onClick={copy}>
          <CopyIcon />
          {t('mailDetails.headers.copy')}
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs break-all whitespace-pre-wrap">{text}</pre>
    </div>
  );
}

const byPrefix = (findings: readonly AnalysisFinding[], ...prefixes: string[]) => findings.filter((f) => prefixes.some((p) => f.code.startsWith(p)));

/** Four concerns share one scrolling body; expert details stay with their related evidence. */
export function MessageDetailsView({ analysis }: { analysis: MessageAnalysisView }) {
  const { t } = useI18n();
  const [expert, setExpert] = useState(false);
  const [tab, setTab] = useState<Tab>('overview');
  const { findings } = analysis;
  const tabs: Tab[] = ['overview', 'senderAuthentication', 'deliverySecurity', 'technical'];
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <StatusBadge status={SEVERITY_STATUS[analysis.status]} label={t(`mailDetails.status.${analysis.status}`)} />
        <Button variant="outline" size="sm" aria-pressed={expert} onClick={() => setExpert(!expert)}>
          {t('mailDetails.expertView')}
        </Button>
      </div>
      <Tabs value={tab} onValueChange={(value) => setTab(value as Tab)} className="min-h-0 flex-1">
        <TabsList>
          {tabs.map((id) => (
            <TabsTrigger key={id} value={id}>
              {t(`mailDetails.tabs.${id}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        <DialogBody>
          <TabsContent value="overview">
            <TabSection explain={t('mailDetails.summary.explain')} findings={findings}>
              {findings.length === 0 && <p className="text-sm text-muted-foreground">{t('mailDetails.summary.noFindings')}</p>}
            </TabSection>
          </TabsContent>
          <TabsContent value="senderAuthentication" className="flex flex-col gap-6">
            <h3 className="text-sm font-semibold">{t('mailDetails.tabs.sender')}</h3>
            <SenderTab analysis={analysis} findings={byPrefix(findings, 'sender.')} />
            <h3 className="text-sm font-semibold">{t('mailDetails.tabs.authentication')}</h3>
            <AuthenticationTab analysis={analysis} findings={byPrefix(findings, 'auth.')} expert={expert} />
          </TabsContent>
          <TabsContent value="deliverySecurity" className="flex flex-col gap-6">
            <h3 className="text-sm font-semibold">{t('mailDetails.tabs.delivery')}</h3>
            <DeliveryTab analysis={analysis} findings={byPrefix(findings, 'delivery.')} expert={expert} />
            <h3 className="text-sm font-semibold">{t('mailDetails.tabs.security')}</h3>
            <SecurityTab analysis={analysis} findings={byPrefix(findings, 'scanner.')} expert={expert} />
          </TabsContent>
          <TabsContent value="technical" className="flex flex-col gap-6">
            <TechnicalTab analysis={analysis} findings={byPrefix(findings, 'source.')} expert={expert} />
            {expert && (
              <>
                <h3 className="text-sm font-semibold">{t('mailDetails.tabs.headers')}</h3>
                <HeadersTab analysis={analysis} />
              </>
            )}
          </TabsContent>
        </DialogBody>
      </Tabs>
    </div>
  );
}

export interface MessageDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Shown under the title. */
  subject?: string;
  analysis?: MessageAnalysisView;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}

/** Shared capped frame keeps the header and four tab controls visible on every screen. */
export function MessageDetailsDialog({ open, onOpenChange, subject, analysis, loading, error, onRetry }: MessageDetailsDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent framed size="xl" className="gap-3">
        <DialogHeader className="pr-8">
          <DialogTitle>{t('mailDetails.title')}</DialogTitle>
          <DialogDescription className="truncate">{subject}</DialogDescription>
        </DialogHeader>
        {error ? (
          <DialogBody><ErrorState onRetry={onRetry} /></DialogBody>
        ) : loading || !analysis ? (
          <DialogBody className="flex flex-col gap-3" aria-busy="true" aria-label={t('mailDetails.loading')}>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
          </DialogBody>
        ) : (
          <MessageDetailsView analysis={analysis} />
        )}
      </DialogContent>
    </Dialog>
  );
}
